// notebookLayout — the notebook's virtual pages (BOLUS_NOTES-18; REQUIREMENTS_NOTEBOOK NB-6).
//
// Pure functions, moved out of Notebooks.tsx unchanged so they can be unit
// tested: given the journal days and how many lines fit on one page for the
// current screen, lay consecutive days out across pages. Nothing about pages is
// stored — the same days give the same pages for the same capacity.

export type Kind = 'bolus' | 'basal' | 'meal' | 'note';
export interface CarbItem { name: string; portion?: string; carbs_g: number; rationale?: string; edited?: boolean }
export interface CarbAnalysis { items: CarbItem[]; total_carbs_g?: number; confidence?: string; notes?: string; model?: string; analyzed_at?: string }
/** The total is always the sum of the (possibly corrected) items. */
export const analysisTotal = (a?: CarbAnalysis | null) => Math.round((a?.items || []).reduce((s, i) => s + (Number(i.carbs_g) || 0), 0) * 10) / 10;
export interface JournalEntry {
  kind: Kind; id: string; timestamp: string; local_time: string; updated_at?: string;
  insulin_units?: number; carbs_consumed?: number; blood_glucose_before?: number; meal_type?: string;
  injection_site?: string; food_description?: string; insulin_type?: string; text?: string; has_photo?: boolean;
  meal_id?: string | null;                 // bolus → its meal (BO-3)
  description?: string; carbs?: number;    // meal (ME-1)
  carb_analysis?: CarbAnalysis | null;      // meal photo analysis (2026-10-12)
}
export interface JournalDay {
  date: string; entries: JournalEntry[];
  cgm: { readings: number; avg: number; min: number; max: number; low_pct: number; in_range_pct: number; high_pct: number } | null;
  drawing: { id: string; has_content: boolean; extracted_text?: string } | null;
  totals: { bolus_units: number; basal_units: number; carbs: number; meals?: number };
}
export interface Journal { profile: { id: string; name: string; timezone: string; can_write?: boolean }; from: string; to: string; today: string; days: JournalDay[] }
export interface Profile { id: string; name: string; latest_reading?: { glucose_value?: number; trend?: string; timestamp?: string } | null }

// A "line" is the unit of page capacity. Each entry costs a few lines.
export interface Line { key: string; kind: 'day' | 'entry' | 'continued' | 'empty' | 'cgm' | 'drawing'; day: JournalDay; entry?: JournalEntry; cost: number }
export interface Page { index: number; lines: Line[]; dates: string[]; continuedFrom: boolean; continuedOn: boolean }

export const entryCost = (e: JournalEntry) => {
  if (e.kind === 'note') return 1 + Math.ceil(((e.text || '').length) / 70) + (e.has_photo ? 3 : 0);
  if (e.kind === 'bolus') return 1 + (e.food_description ? 1 : 0);
  if (e.kind === 'meal') return 1 + (e.description && e.description.length > 50 ? 1 : 0);
  return 1;
};

/** Lay days out over pages of `capacity` lines. Deterministic for a given capacity. */
export function layout(days: JournalDay[], capacity: number): Page[] {
  const pages: Page[] = [];
  let cur: Page = { index: 0, lines: [], dates: [], continuedFrom: false, continuedOn: false };
  let used = 0;
  const flush = () => { if (cur.lines.length) { pages.push(cur); cur = { index: pages.length, lines: [], dates: [], continuedFrom: false, continuedOn: false }; used = 0; } };
  const push = (l: Line) => {
    // Keep a day header with at least its first two lines (no orphan headers at a page foot).
    const needs = l.kind === 'day' ? l.cost + 2 : l.cost;
    if (used + needs > capacity && used > 0) {
      const midDay = l.kind !== 'day';           // a clean day boundary is not a continuation
      cur.continuedOn = midDay; flush(); cur.continuedFrom = midDay;
      // Re-state the day header on the continuation page so the reader knows where they are.
      if (l.kind === 'entry') { cur.lines.push({ key: `${l.day.date}-cont`, kind: 'continued', day: l.day, cost: 1 }); used += 1; }
    }
    if (!cur.dates.includes(l.day.date)) cur.dates.push(l.day.date);
    cur.lines.push(l); used += l.cost;
  };
  for (const d of days) {
    push({ key: `${d.date}-day`, kind: 'day', day: d, cost: 2 });
    if (d.cgm) push({ key: `${d.date}-cgm`, kind: 'cgm', day: d, cost: 1 });
    // NB-9: a day with an iPad/iPhone hand drawing says so (with its recognised text).
    if (d.drawing?.has_content) push({ key: `${d.date}-drawing`, kind: 'drawing', day: d, cost: 1 });
    if (d.entries.length === 0 && !d.drawing?.has_content) push({ key: `${d.date}-empty`, kind: 'empty', day: d, cost: 1 });
    for (const e of d.entries) push({ key: `${d.date}-${e.kind}-${e.id}`, kind: 'entry', day: d, entry: e, cost: entryCost(e) });
  }
  flush();
  return pages;
}

